import { DeeplinkKeys } from '@/consts/deepLinks';
import extractUrlParams from '@/utils/ui/extractUrlParams';

import { DeeplinkKind, type Deeplink } from './types';

const parseSocialShareFileUrl = (
  rawState: string | undefined
): string | null => {
  if (!rawState) return null;

  const parsed: { fileUrl?: unknown; miniapp?: unknown } | null =
    JSON.parse(rawState);
  if (
    parsed &&
    typeof parsed.fileUrl === 'string' &&
    parsed.fileUrl &&
    parsed.miniapp === true
  ) {
    return parsed.fileUrl;
  }

  return null;
};

/**
 * Priority-ordered table mapping URL params to typed deeplinks.
 * Adding a new deeplink kind means one entry here + one branch in the
 * Deeplink union — no changes to consumers.
 *
 * Priority is encoded by array order:
 *   task > projectJson > preset > category > tool
 */
const PRIORITY: Array<{
  key: DeeplinkKeys;
  build: (
    value: string,
    params: Partial<Record<DeeplinkKeys, string>>
  ) => Deeplink;
}> = [
  {
    key: DeeplinkKeys.taskId,
    build: (taskId) => ({ kind: DeeplinkKind.Task, taskId }),
  },
  {
    key: DeeplinkKeys.presetJsonUrl,
    build: (jsonUrl) => ({ kind: DeeplinkKind.ProjectJson, jsonUrl }),
  },
  {
    key: DeeplinkKeys.presetId,
    build: (presetId, params) => ({
      kind: DeeplinkKind.Preset,
      presetId,
      category: params[DeeplinkKeys.category],
    }),
  },
  {
    key: DeeplinkKeys.category,
    build: (category) => ({ kind: DeeplinkKind.Category, category }),
  },
  {
    key: DeeplinkKeys.tool,
    build: (tool) => ({ kind: DeeplinkKind.Tool, tool }),
  },
];

const parseDeeplinks = (): Deeplink => {
  const rawState = extractUrlParams([DeeplinkKeys.socialShareState])[
    DeeplinkKeys.socialShareState
  ];
  const socialShareFileUrl = parseSocialShareFileUrl(rawState);
  if (socialShareFileUrl) {
    return { kind: DeeplinkKind.SocialShare, fileUrl: socialShareFileUrl };
  }

  const params = extractUrlParams(PRIORITY.map((entry) => entry.key));
  const match = PRIORITY.find(({ key }) => params[key]);
  if (!match) return { kind: DeeplinkKind.None };
  return match.build(params[match.key] as string, params);
};

export default parseDeeplinks;
